import type { BoardCapacity } from '@molt/shared';
import { Notice } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { ReactElement } from 'react';

type CapacityEntry = BoardCapacity['capacities'][number];
type CapacityTranslate = ReturnType<typeof useInterfaceLanguage>['t'];

const CAPACITY_LABEL_KEYS: Record<CapacityEntry['kind'], string> = {
  open_cards: 'boardCapacity.kind.openCards',
  total_cards: 'boardCapacity.kind.totalCards',
  open_lists: 'boardCapacity.kind.openLists',
  total_lists: 'boardCapacity.kind.totalLists',
  attachments_per_card: 'boardCapacity.kind.attachmentsPerCard',
  checklists_per_card: 'boardCapacity.kind.checklistsPerCard',
  checklists_per_board: 'boardCapacity.kind.checklistsPerBoard',
  labels_per_board: 'boardCapacity.kind.labelsPerBoard',
  members_per_board: 'boardCapacity.kind.membersPerBoard',
};

export function capacityKindLabel(
  t: CapacityTranslate,
  kind: CapacityEntry['kind'],
): string {
  return t(CAPACITY_LABEL_KEYS[kind]);
}

export function capacityMessage(t: CapacityTranslate, entry: CapacityEntry): string {
  const values = {
    current: entry.current.toLocaleString(),
    limit: entry.limit.toLocaleString(),
    kind: capacityKindLabel(t, entry.kind),
    warningAt: entry.warningAt.toLocaleString(),
  };
  if (entry.status === 'at_limit') {
    return t('boardCapacity.atLimit', values);
  }
  return t('boardCapacity.warning', values);
}

export interface BoardCapacityNoticeProps {
  readonly boardTitle: string;
  readonly capacity?: BoardCapacity | null | undefined;
  readonly compact?: boolean | undefined;
}

export function BoardCapacityNotice({
  boardTitle,
  capacity,
  compact = false,
}: BoardCapacityNoticeProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const entries = capacity?.capacities.filter((entry) => entry.status !== 'ok') ?? [];
  if (entries.length === 0) return null;

  return (
    <Notice
      tone={entries.some((entry) => entry.status === 'at_limit') ? 'bad' : 'warn'}
      title={
        compact
          ? t('boardCapacity.titleCompact')
          : t('boardCapacity.title', { board: boardTitle })
      }
      body={entries.map((entry) => capacityMessage(t, entry)).join(' ')}
    />
  );
}
